// src/components/PianoKeyMapCard.js
import React, { useMemo, useState } from "react";

/** Normalize tokens like "C5", "Bb4", "A#" -> "C", "A#", "A#" */
function normalize(note) {
  if (!note) return null;
  const raw = String(note).trim().toUpperCase();
  let n = raw.replace(/[0-9\s]/g, ""); // drop octave numbers/spaces
  // flats -> sharps
  const flatMap = { DB: "C#", EB: "D#", GB: "F#", AB: "G#", BB: "A#" };
  if (n.length === 2 && n[1] === "B") n = flatMap[n] || n;
  return n;
}

/**
 * Percent XY centers for the current image asset (/images/PianoKeysMap.png).
 * PAD is calibrated to the rounded frame margin in the PNG so that centers land
 * visually on the middle of each key cap. If you ever swap the image, tweak PAD.
 */
function useKeyCenters() {
  const PAD = 4.9; // tuned for this PNG; increase => shifts centers slightly right
  const seg = (100 - 2 * PAD) / 7;

  // white key centers (C, D, E, F, G, A, B)
  const whiteCenters = Array.from(
    { length: 7 },
    (_, i) => PAD + (i + 0.5) * seg
  );

  // black key centers between whites
  const blackCenters = {
    CSharp: (whiteCenters[0] + whiteCenters[1]) / 2,
    DSharp: (whiteCenters[1] + whiteCenters[2]) / 2,
    FSharp: (whiteCenters[3] + whiteCenters[4]) / 2,
    GSharp: (whiteCenters[4] + whiteCenters[5]) / 2,
    ASharp: (whiteCenters[5] + whiteCenters[6]) / 2,
  };

  return { whiteCenters, blackCenters };
}

export default function PianoKeyMapCard({
  expectedNote, // raw token (e.g., "C5", "Bb", "A#")
  caption = "PIANO KEYS KEY MAP",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const note = normalize(expectedNote);
  const { whiteCenters, blackCenters } = useKeyCenters();

  const pos = useMemo(() => {
    if (!note) return null;
    const whiteOrder = ["C", "D", "E", "F", "G", "A", "B"];
    const isBlack = ["C#", "D#", "F#", "G#", "A#"].includes(note);

    if (isBlack) {
      const keyMap = {
        "C#": blackCenters.CSharp,
        "D#": blackCenters.DSharp,
        "F#": blackCenters.FSharp,
        "G#": blackCenters.GSharp,
        "A#": blackCenters.ASharp,
      };
      return { x: keyMap[note], y: 30, dot: 14, ring: 28 };
    }

    const idx = whiteOrder.indexOf(note);
    if (idx === -1) return null;
    return { x: whiteCenters[idx], y: 78, dot: 14, ring: 28 };
  }, [note, whiteCenters, blackCenters]);

  /** Centered pulse marker (used in both card and lightbox) */
  const Marker = ({ sizeMul = 1 }) =>
    pos ? (
      <div
        className="absolute"
        style={{
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <div
          className="relative"
          style={{ width: pos.ring * sizeMul, height: pos.ring * sizeMul }}
        >
          {/* ping ring */}
          <div className="absolute inset-0 rounded-full border-2 border-blue-500/70 animate-ping" />
          {/* solid center dot */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-400/70 shadow"
            style={{ width: pos.dot * sizeMul, height: pos.dot * sizeMul }}
          />
        </div>
      </div>
    ) : null;

  return (
    <div
      className={`rounded-xl border border-purple-200 bg-white/90 shadow-sm p-3 ${className}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold text-purple-800">{caption}</div>
        <button
          onClick={() => setOpen(true)}
          className="text-[10px] px-2 py-[2px] rounded-md bg-purple-100 hover:bg-purple-200"
          title="Enlarge"
        >
          Enlarge
        </button>
      </div>

      {/* Image with overlay */}
      <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 bg-white">
        <img
          src="/images/PianoKeysMap.png"
          alt="One-octave piano key guide with labels"
          className="block w-full h-auto select-none"
          draggable="false"
        />
        <Marker sizeMul={1} />
      </div>

      <div className="mt-1 text-[10px] text-gray-600">
        Tip: <b>C</b> is the white key just to the left of the two black keys.
        {note ? (
          <span className="ml-2">
            Current note: <b>{note}</b>
          </span>
        ) : null}
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="max-w-4xl w-full rounded-2xl bg-white shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-purple-800">
                {caption}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-1 rounded-md bg-purple-100 hover:bg-purple-200 text-purple-800 text-xs"
              >
                Close
              </button>
            </div>

            <div className="relative rounded-xl overflow-hidden border border-gray-200">
              <img
                src="/images/PianoKeysMap.png"
                alt="Piano key guide enlarged"
                className="block w-full h-auto select-none"
                draggable="false"
              />
              {/* Larger marker for the lightbox */}
              <Marker sizeMul={1.8} />
            </div>

            <div className="mt-3 text-[12px] text-gray-600">
              Sharps (<b>#</b>) are the black keys. There’s no black key
              between <b>E–F</b> or <b>B–C</b>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
